import {
  isPlaybookCriterionNode,
  type FlattenedPlaybookNode,
  type PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";

/** The direction the criterion arrows move through the flattened tree. */
type FeedbackReviewNavigationDirection = "previous" | "next";

/**
 * Only the `criteria_node_id` and `status` of a feedback row matter here, so
 * the helpers take the narrowest shape callers can satisfy.
 */
type FeedbackNodeStatus = Pick<
  FeedbackWithCurrentGrade,
  "criteria_node_id" | "status"
>;

/** How much unresolved feedback each node carries, keyed by node id. */
export function buildPlaybookOpenFeedbackCounts(
  feedback: readonly FeedbackNodeStatus[],
): Map<PlaybookNodeId, number> {
  const counts = new Map<PlaybookNodeId, number>();
  for (const item of feedback) {
    if (item.status !== "open") {
      continue;
    }
    counts.set(
      item.criteria_node_id,
      (counts.get(item.criteria_node_id) ?? 0) + 1,
    );
  }
  return counts;
}

/**
 * The next/previous node in tree order that still has open feedback, or null at
 * either end. Deliberately does not wrap around: the arrows are a walk through
 * the review queue, and wrapping would make "no more left" indistinguishable
 * from starting over.
 *
 * Works from a `currentNodeId` that has no open feedback of its own (the user
 * can jump to any node), but returns null for an id that is not in the tree.
 */
export function getAdjacentPlaybookNodeIdWithOpenFeedback(
  flattenedNodes: readonly FlattenedPlaybookNode[],
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>,
  currentNodeId: PlaybookNodeId | null,
  direction: FeedbackReviewNavigationDirection,
): PlaybookNodeId | null {
  const currentIndex = flattenedNodes.findIndex(
    (entry) => entry.node.id === currentNodeId,
  );
  if (currentIndex === -1) {
    return null;
  }

  const step = direction === "next" ? 1 : -1;
  for (
    let index = currentIndex + step;
    index >= 0 && index < flattenedNodes.length;
    index += step
  ) {
    const entry = flattenedNodes[index];
    if (entry && (openFeedbackCounts.get(entry.node.id) ?? 0) > 0) {
      return entry.node.id;
    }
  }
  return null;
}

/**
 * Where the node sits in the review queue — its 1-based position among the
 * nodes with open feedback, in tree order, and how many there are. Null when
 * the node has no open feedback of its own (the user can jump to any node),
 * so the panel falls back to a plain label.
 */
export function getPlaybookOpenFeedbackPosition(
  flattenedNodes: readonly FlattenedPlaybookNode[],
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>,
  nodeId: PlaybookNodeId | null,
): { position: number; total: number } | null {
  const openNodeIds = flattenedNodes
    .map((entry) => entry.node.id)
    .filter((id) => (openFeedbackCounts.get(id) ?? 0) > 0);

  const index = nodeId === null ? -1 : openNodeIds.indexOf(nodeId);
  if (index === -1) {
    return null;
  }
  return { position: index + 1, total: openNodeIds.length };
}

/**
 * Where the review tab starts: the first node holding open feedback, falling
 * back to the first criterion so an all-resolved playbook still shows
 * something. Null only for a playbook with no criteria at all.
 */
export function getInitialFeedbackReviewNodeId(
  flattenedNodes: readonly FlattenedPlaybookNode[],
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>,
): PlaybookNodeId | null {
  const withOpenFeedback = flattenedNodes.find(
    (entry) => (openFeedbackCounts.get(entry.node.id) ?? 0) > 0,
  );
  if (withOpenFeedback) {
    return withOpenFeedback.node.id;
  }

  const firstCriterion = flattenedNodes.find((entry) =>
    isPlaybookCriterionNode(entry.node),
  );
  return firstCriterion?.node.id ?? null;
}
